import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { getAllInternalMartinusSchools } from '../../groups/api/getAllSchools.js';
import type { Role } from '../roles.js';
import type { UserWithPermissionFieldsRoles } from '../UserWithPermissionFields.js';

/**
 * Gets the school IDs for the given user and roles. As a context can have multiple _ids where some can be schools or persons we need to explicitly check if the context is a school.
 * @param user The user to get the school IDs for
 * @param roles The roles to check
 * @returns The school IDs for the given user and roles
 */
export async function getSchoolIDsForRoles(
  user: Maybe<UserWithPermissionFieldsRoles>,
  roles: Role[],
): Promise<string[]> {
  if (!user) {
    return [];
  }
  const userRoles = user.roles?.filter(r => roles.includes(r.name as Role));
  if (!userRoles || userRoles.length === 0) {
    return [];
  }
  const allSchools = (await getAllInternalMartinusSchools()).map(s => s._id);
  const result = Array.from(
    new Set(userRoles?.flatMap(r => r.contexts?.filter(c => allSchools.includes(c))) ?? []),
  ).filter(isNotNullish);
  return result;
}

/**
 * Checks if the current user and the viewed user have any common contexts for the given roles.
 * @param currentUser The current user
 * @param viewedUser The user being viewed
 * @param rolesCurrentUser The roles of the current user to check
 * @param rolesViewedUser The roles of the viewed user to check
 * @returns true if the users have common contexts, false otherwise
 */
export async function usersHaveSameSchoolOnRoles({
  currentUser,
  viewedUser,
  rolesCurrentUser,
  rolesViewedUser,
}: {
  currentUser: Maybe<UserWithPermissionFieldsRoles>;
  viewedUser: Maybe<UserWithPermissionFieldsRoles>;
  rolesCurrentUser: Role[];
  rolesViewedUser: Role[];
}): Promise<boolean> {
  /**
   * If the currentUser has a global context he "is" at the school of the viewed user. As he is on all schools
   */
  const currentUserHasGlobalContext = currentUser?.roles?.some(r => r.contexts?.includes(GLOBAL_CONTEXT));
  if (currentUserHasGlobalContext) {
    return true;
  }

  const currentUserContexts = await getSchoolIDsForRoles(currentUser, rolesCurrentUser);
  const viewedUserContexts = await getSchoolIDsForRoles(viewedUser, rolesViewedUser);
  return currentUserContexts.some(context => viewedUserContexts.includes(context));
}
